import type { Me } from '@spot/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ScreenMessage } from '../../components/ui/screen-message';
import { Splash } from '../../components/ui/splash';
import { setUiLocale } from '../../i18n';
import { trackSignedIn } from '../../lib/analytics';
import { ApiError, type ApiClient } from '../../lib/api';
import { readEnv } from '../../lib/env';
import { markTelegramReady, openTelegramChat } from '../../lib/telegram';
import type { Session } from './session';

interface SessionValue {
	api: ApiClient;
	me: Me;
	analyticsId: string;
	isNewUser: boolean;
	/** After `DELETE /v1/me`: ends the session and shows the «account deleted» screen. */
	onAccountDeleted: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
	const value = useContext(SessionContext);
	if (value === null) throw new Error('useSession must be used inside <SessionGate>');
	return value;
}

/** Runs silent auth, shows the splash meanwhile, and renders children once signed in. */
export function SessionGate({
	session,
	insideTelegram,
	children,
}: {
	session: Session;
	insideTelegram: boolean;
	children: ReactNode;
}) {
	const query = useQuery({
		queryKey: ['session'],
		queryFn: async () => {
			const signedIn = await session.login();
			// Once per app start (token refreshes re-login without this query).
			trackSignedIn(signedIn.analyticsId, signedIn.isNewUser);
			return signedIn;
		},
		enabled: insideTelegram,
		retry: false,
		staleTime: Infinity,
		gcTime: Infinity,
	});

	const [ended, setEnded] = useState<'deleted' | 'banned' | null>(null);
	const [restoredSeen, setRestoredSeen] = useState(false);

	// T-1004 follow-up: the account was deleted or banned elsewhere (another device, a moderator).
	// The API then refuses every call with 403; end the session (no silent re-login, which would
	// restore a deleted account) and show why, instead of a generic error on each screen.
	const queryClient = useQueryClient();
	useEffect(() => {
		const onError = (error: unknown) => {
			if (!(error instanceof ApiError)) return;
			if (error.code !== 'account_deleted' && error.code !== 'account_banned') return;
			session.end();
			setEnded(error.code === 'account_deleted' ? 'deleted' : 'banned');
		};
		const offQueries = queryClient.getQueryCache().subscribe((event) => {
			if (event.type === 'updated' && event.action.type === 'error') onError(event.action.error);
		});
		const offMutations = queryClient.getMutationCache().subscribe((event) => {
			if (event.type === 'updated' && event.action.type === 'error') onError(event.action.error);
		});
		return () => {
			offQueries();
			offMutations();
		};
	}, [queryClient, session]);

	useEffect(() => {
		if (query.data !== undefined) void setUiLocale(query.data.user.locale);
	}, [query.data]);

	useEffect(() => {
		if (!query.isPending || !insideTelegram) markTelegramReady();
	}, [query.isPending, insideTelegram]);

	if (!insideTelegram) return <OutsideTelegram />;
	if (ended === 'deleted') return <AccountDeleted />;
	if (ended === 'banned') return <AccountBanned />;
	if (query.isPending) return <Splash />;
	if (query.isError) return <AuthError error={query.error} onRetry={() => void query.refetch()} />;
	if (query.data.restored && !restoredSeen) {
		return (
			<AccountRestored
				onContinue={() => {
					setRestoredSeen(true);
				}}
			/>
		);
	}

	const value: SessionValue = {
		api: session.api,
		me: query.data.user,
		analyticsId: query.data.analyticsId,
		isNewUser: query.data.isNewUser,
		onAccountDeleted: () => {
			session.end();
			setEnded('deleted');
		},
	};
	return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

function OutsideTelegram() {
	const { t } = useTranslation();
	return (
		<ScreenMessage
			title={t('errors.open_in_telegram_title')}
			body={t('errors.open_in_telegram_body')}
		/>
	);
}

function AccountDeleted() {
	const { t } = useTranslation();
	return <ScreenMessage title={t('account.deleted_title')} body={t('account.deleted_body')} />;
}

function AccountBanned() {
	const { t } = useTranslation();
	const support = useMemo(() => readEnv().VITE_SUPPORT_TELEGRAM, []);
	return (
		<ScreenMessage
			title={t('account.banned_title')}
			body={t('account.banned_body')}
			{...(support === undefined
				? {}
				: {
						action: {
							label: t('account.banned_support'),
							onClick: () => {
								openTelegramChat(support);
							},
						},
					})}
		/>
	);
}

function AccountRestored({ onContinue }: { onContinue: () => void }) {
	const { t } = useTranslation();
	return (
		<ScreenMessage
			title={t('account.restored_title')}
			body={t('account.restored_body')}
			action={{ label: t('account.restored_continue'), onClick: onContinue }}
		/>
	);
}

function AuthError({ error, onRetry }: { error: Error; onRetry: () => void }) {
	const { t } = useTranslation();
	const code = error instanceof ApiError ? error.code : null;

	if (code === 'init_data_expired' || code === 'init_data_invalid') {
		return <ScreenMessage title={t('errors.reopen_title')} body={t('errors.reopen_body')} />;
	}
	if (code === 'account_banned') {
		return <ScreenMessage title={t('errors.generic_title')} body={t('errors.account_banned')} />;
	}
	if (code === 'account_deleted') {
		return <ScreenMessage title={t('errors.generic_title')} body={t('errors.account_deleted')} />;
	}
	return (
		<ScreenMessage
			title={t('errors.generic_title')}
			body={code === 'network_error' ? t('errors.network') : t('errors.generic_body')}
			action={{ label: t('errors.retry'), onClick: onRetry }}
		/>
	);
}
